import { useCallback, useEffect, useRef } from 'react'

type InactiveTabProps = {
    timeout?: number
    onInactive?: () => void
    onActive?: () => void
}

/**
 * Detects when the page/tab has been inactive for a given duration.
 *
 * Includes:
 * - Hidden/background tabs
 * - Minimized browser windows
 * - No user interaction
 *
 * If no `timeout` is provided, no inactive tab will be handled.
 */
export function useInactiveTab({
    timeout,
    onInactive,
    onActive,
}: InactiveTabProps) {
    const timeoutId = useRef<number | null>(null)
    const isInactive = useRef(false)

    // Clear any existing timeout to prevent multiple timers running simultaneously
    const clearExistingTimeout = useCallback(() => {
        if (timeoutId.current !== null) {
            window.clearTimeout(timeoutId.current)
        }
    }, [])

    // Set the inactive state and call the onInactive callback if provided
    const triggerInactive = useCallback(() => {
        // If the tab is already marked as inactive, do nothing to prevent multiple calls to onInactive
        if (isInactive.current) {
            return
        }

        isInactive.current = true
        onInactive?.()
    }, [onInactive])

    // Reset the inactivity timer and call the onActive callback if the user becomes active again
    const resetTimer = useCallback(() => {
        clearExistingTimeout()

        // If the user becomes active again
        if (isInactive.current) {
            isInactive.current = false
            onActive?.()
        }

        timeoutId.current = window.setTimeout(() => {
            triggerInactive()
        }, timeout)
    }, [clearExistingTimeout, onActive, timeout, triggerInactive])

    // Set up event listeners for user activity and visibility changes
    useEffect(() => {
        // If no timeout is provided, the hook won't set up any timers or event listeners, effectively disabling the inactivity detection. This allows components to use the hook without enforcing inactivity behavior if they choose not to provide a timeout value.
        if (timeout === undefined) {
            return
        }

        const activityEvents = [
            'mousemove',
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
        ]

        const handleVisibilityChange = () => {
            // Immediately start inactivity countdown
            // when tab becomes hidden/backgrounded
            if (document.hidden) {
                clearExistingTimeout()

                timeoutId.current = window.setTimeout(() => {
                    triggerInactive()
                }, timeout)

                return
            }

            // Tab became visible again
            resetTimer()
        }

        const handleUserActivity = () => {
            // Ignore activity if tab is hidden
            if (document.hidden) {
                return
            }

            resetTimer()
        }

        // Start timer immediately
        resetTimer()

        activityEvents.forEach((event) => {
            window.addEventListener(event, handleUserActivity)
        })

        document.addEventListener('visibilitychange', handleVisibilityChange)

        // Clean up event listeners and timers on unmount
        return () => {
            clearExistingTimeout()

            activityEvents.forEach((event) => {
                window.removeEventListener(event, handleUserActivity)
            })

            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
        }
    }, [clearExistingTimeout, resetTimer, timeout, triggerInactive])
}
